/**
 * Theme store — Zustand.
 *
 * Тема интерфейса: светлая (по умолчанию) и тёмная. Сами цвета лежат в
 * CSS-переменных (см. src/index.css), поэтому переключение — это один класс
 * .dark на <html>, без перерисовки компонентов и без dark:-вариантов в разметке.
 *
 * Выбор хранится в localStorage под ключом cs-theme. Тот же ключ читает
 * инлайн-скрипт в index.html — он ставит класс до первой отрисовки, иначе при
 * загрузке была бы вспышка светлой темы у тех, кто работает в тёмной.
 *
 * Приватный режим браузера: localStorage бросает — тема работает до перезагрузки.
 */
import { create } from 'zustand';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'cs-theme';

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: readStoredTheme(),

  setTheme: (theme) => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* приватный режим — выбор не переживёт перезагрузку, это ожидаемо */
    }
    set({ theme });
  },

  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));

// Класс уже мог поставить инлайн-скрипт из index.html — приводим состояние в
// соответствие с реальным DOM, чтобы иконка тумблера не расходилась с темой.
applyTheme(useThemeStore.getState().theme);
